import React, { useEffect, useMemo, useState } from 'react';
import { Image, message } from 'antd';
import { useCookies } from 'react-cookie';
import { useNavigate } from 'react-router-dom';
import DefaultLayout from 'components/layout/DefaultLayout';
import NoImage from 'components/etc/NoImage';
import MainTable from 'components/table/MainTable';
import TableToolbar from 'components/table/TableToolbar';
import SearchInput from 'components/search/SearchInput';
import ProjectRegisterModal from './ProjectRegisterModal';
import TextButton from 'components/button/TextButton';
import { storyApi } from 'apis/story/storyApis';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { theme } from 'utils/style/theme';
import PrimaryButton from 'components/button/PrimaryButton';
import plusIcon from 'utils/icon/icon_plus-white.png';
import listIcon from 'utils/icon/icon_list-black.png';
import categoryIcon from 'utils/icon/icon_category-black.png';
import ListTable from 'components/table/ListTable';
import {
  queryDropdownBubbleList,
  queryProduceLang,
} from 'apis/common/commonApis';
import ActionButton from '../../../components/table/ActionButton';
import { SecurityUser, Trash } from 'iconsax-react';
import ProjectAuthModal from './ProjectAuthModal';
import {
  queryFetchOrganizationUsers,
  queryFetchProjectUsers,
  userApi,
} from '../../../apis/user/userApi';
import { useAppSelector } from '../../../hooks/redux';
import { IAccount, UserRole } from '../../../utils/types/common';
import DeleteModal from '../../../components/modal/DeleteModal';
import { episodeApi } from '../../../apis/story/episodeApis';
import { IProject } from '../../../utils/types/storyTypes';
/**
 * * 작품 관리 - 전체 리스트
 */

const ProjectTotalListPage = () => {
  const { t } = useTranslation();
  const [cookies] = useCookies();
  const navigate = useNavigate();
  const user = useAppSelector((state) => state.userSlice.userInfor);
  const [visible, setVisible] = useState(false);
  const [searchValue, setSearchValue] = useState<string>('');
  const [totalStoryList, setTotalList] = useState<any>();
  const [searchStoryList, setSearchStoryList] = useState<any>();
  const [openStoryAuthModal, setOpenStoryAuthModal] = useState<boolean>(false);
  const [displayType, setDisplayType] = useState<string>('list');

  const { data: bubbleList } = queryDropdownBubbleList('KO'); // 말풍선리스트
  const { data: produceLangList } = queryProduceLang(); // 제작지원리스트
  const {
    data: organizationUsers,
    error,
    isLoading,
    isError,
  } = queryFetchOrganizationUsers(user?.organization.name ?? ' '); // 조직사용자리스트
  const [newOrganizationUsers, setNewOrganizationUsers] = useState<IAccount[]>(
    [],
  );
  const [currentProjectUsers, setCurrentProjectUsers] = useState<IAccount[]>(
    [],
  );
  const [currentProjectId, setCurrentProjectId] = useState<number>(0);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedProject, setSelectedProject] = useState<IProject | null>(null);

  /// 에피소드 삭제
  const handleDelete = async () => {
    if (selectedProject) {
      try {
        setDeleteLoading(true);
        await storyApi.delete(selectedProject.project_id);
        fetchData();
        message.success(`${selectedProject.title} 가(이) 삭제되었습니다.`);
        setDeleteLoading(false);
        setDeleteModal(false);
      } catch (err) {
        setDeleteLoading(false);
      }
    }
  };

  useEffect(() => {
    if (organizationUsers) {
      setNewOrganizationUsers(organizationUsers);
    }
  }, [organizationUsers]);

  useEffect(() => {
    console.log('ProjectTotalListPage.tsx useEffect');
  }, []);

  /// 모달 오픈
  const openStoryRegisterPopup = () => {
    setVisible(true);
  };

  // * 상세화면으로 이동 ProjectDetailPage
  const handleLink = (project) => {
    // record는 프로젝트..
    navigate(`story/${project?.project_id}/episode`, {
      state: { record: project, project: project, bubbleList },
    });
  };

  const handleProjectAuthClicked = async (project) => {
    const projectUsers = await userApi.fetchProjectUsers(
      user?.organization.name ?? ' ',
      project.project_id,
    );
    setOpenStoryAuthModal(true);
    setCurrentProjectUsers(projectUsers);
    setCurrentProjectId(project.project_id);
  };

  const ImageThumbnail = React.memo(({ record }: { record: any }) => {
    const findImageUrl = record?.projectDetails?.find(
      (e) => e.lang === cookies.lang,
    );
    if (!findImageUrl) {
      if (
        record?.icon_url === null ||
        record?.icon_url === undefined ||
        record?.icon_url === ''
      ) {
        return <NoImage height='70px' width='60px' />;
      }
      return (
        <Image
          src={record?.icon_url}
          style={{
            width: '70px',
            height: '70px',
            margin: 'auto',
          }}
        />
      );
    } else {
      if (
        findImageUrl?.icon_url === null ||
        findImageUrl?.icon_url === undefined ||
        findImageUrl?.icon_url === ''
      ) {
        return <NoImage height='70px' width='60px' />;
      }
      return (
        <Image
          src={findImageUrl?.icon_url}
          style={{
            width: '70px',
            height: '70px',
            margin: 'auto',
          }}
        />
      );
    }
  });

  const tableActionMenu = (record) => {
    return [
      {
        key: 1,
        item: (
          <SecurityUser
            size='24'
            onClick={() => handleProjectAuthClicked(record)}
          />
        ),
        hoverColor: theme.colors.green_500,
        color: theme.colors.dark_200,
      },
      {
        key: 2,
        item: (
          <Trash
            size='24'
            onClick={() => {
              setSelectedProject(record);
              setDeleteModal(true);
            }}
          />
        ),
        hoverColor: theme.colors.red_500,
        color: theme.colors.dark_200,
      },
    ];
  };

  // 작품 리스트 컬럼
  const columnsOrigin = [
    {
      title: (
        <div className='sortWrapper'>
          <p>ID</p>
        </div>
      ),
      dataIndex: 'project_id',
      width: 'fit-content',
      align: 'center',
      defaultSortOrder:
        cookies?.sorterField === 'project_id' ? cookies?.sorterType : undefined,
      sorter: (a, b) => a.project_id - b.project_id,
    },
    {
      title: '',
      dataIndex: 'project_id',
      align: 'center',
      width: '70px',
      render: (text, record) => {
        return (
          <IDColWarpper>
            <ImageContainer>
              <ImageThumbnail record={record} />
            </ImageContainer>
          </IDColWarpper>
        );
      },
    },
    {
      title: (
        <div className='sortWrapper'>
          <p>{t('story.table.title')}</p>
        </div>
      ),
      dataIndex: 'title',
      width: '18rem',
      defaultSortOrder:
        cookies?.sorterField === 'title' ? cookies?.sorterType : undefined,
      sorter: (a, b) => a.title.localeCompare(b.title),
      render: (text, record) => {
        const titleTranslate = record.projectDetails.find(
          (e) => e.lang === cookies.lang,
        );
        return (
          <TextButton onClick={() => handleLink(record)}>
            {titleTranslate ? titleTranslate?.title : text}
          </TextButton>
        );
      },
    },
    {
      title: (
        <div className='sortWrapper'>
          <p>{t('story.table.status')}</p>
        </div>
      ),
      dataIndex: 'project_state',
      width: '18rem',
      render: (text, record) => {
        return (
          <div className='statusTag'>
            <p>{text}</p>
          </div>
        );
      },
      // defaultSortOrder:
      //   cookies?.sorterField === 'title' ? cookies?.sorterType : undefined,
      // sorter: (a, b) => a.title.localeCompare(b.title),
    },
    {
      title: <p>{t('story.table.bubble')}</p>,
      dataIndex: 'bubble_set_id',
      width: '18rem',
      render: (_, record) => {
        const bubbleSetName = bubbleList?.find((e) => e.code === _);
        return (
          <SpeechText>
            <p>{bubbleSetName?.code_name}</p>
          </SpeechText>
        );
      },
    },
    {
      title: <p>{t('story.table.lang')}</p>,
      dataIndex: 'default_lang',
      width: '18rem',
      render: (_, record) => {
        const lang = produceLangList?.find((e) => e.code === _);
        return <p>{lang?.code_name}</p>;
      },
    },
    {
      title: <p>{t('manager.table.userOrganization')}</p>,
      dataIndex: 'organizationName',
      width: '18rem',
    },
    {
      title: '',
      width: '5rem',
      align: 'center',
      render: (text, record) => {
        if (
          user?.role === UserRole.Admin ||
          user?.role === UserRole.SuperAdmin
        ) {
          const menus = [tableActionMenu(record)];
          return <ActionButton menus={menus} />;
        }
      },
    },
  ];

  const fetchData = async () => {
    // const res = await storyApi.read(cookies.lang, user_id, master_auth);
    const res = await storyApi.list();
    setTotalList(res.projects);
    setSearchStoryList(res.projects);
  };

  useEffect(() => {
    fetchData().catch(console.log);
  }, []);

  function filterProjects(searchInput: string) {
    if (!searchInput.length) {
      setSearchStoryList(totalStoryList);
    } else {
      // Convert the search input to lowercase for case-insensitive matching
      const searchTerm = searchInput.toLowerCase();

      // Filter projects
      const filteredProjects = totalStoryList.filter((project) => {
        // Check if any projectDetail's title (if not null) contains the search term
        return project.projectDetails.some((detail) => {
          return (
            detail.title && detail.title.toLowerCase().includes(searchTerm)
          );
        });
      });
      setSearchStoryList(filteredProjects);
    }
  }

  useEffect(() => {
    if (totalStoryList) {
      filterProjects(searchValue);
    }
  }, [searchValue]);

  useEffect(() => {
    if (totalStoryList) {
      setSearchStoryList(totalStoryList);
      filterProjects(searchValue);
    }
  }, [totalStoryList]);

  return (
    <>
      <DefaultLayout>
        <TableToolbar>
          <SearchWrapper>
            <p>{t('constants.menuListName.workManagement')}</p>
            <SearchInputWrapper>
              <SearchInput
                placeholder={t('story.header.searchInputPlaceholder')}
                setSearchData={setSearchValue}
              />
            </SearchInputWrapper>
          </SearchWrapper>
          <PrimaryButton
            text={t('story.header.projectRegistration')}
            height='40px'
            width='fit-content'
            bgColor={theme.colors.blue_500}
            textColor='#ffffff'
            fontWeight={theme.fontWeights.medium}
            fontSize={theme.fontSizes.normal}
            border={`1px solid ${theme.colors.blue_500}`}
            rounded='6px'
            hoverBgColor={theme.colors.blue_600}
            activeBgColor={theme.colors.blue_800}
            selectedBgColor={theme.colors.blue_700}
            onClick={openStoryRegisterPopup}
            RightPlaceHolder={<img src={plusIcon} alt='plus icon' />}
          />
        </TableToolbar>
        <TableListWrapper>
          <div
            className={`${displayType === 'list' ? 'category' : 'uncategory'}`}
            onClick={() => setDisplayType('list')}
          >
            <img src={listIcon} alt='list icon' />
          </div>
          <div
            className={`${
              displayType === 'category' ? 'category' : 'uncategory'
            }`}
            onClick={() => setDisplayType('category')}
          >
            <img src={categoryIcon} alt='list icon' />
          </div>
        </TableListWrapper>
        {displayType === 'category' ? (
          <ListTable data={searchStoryList} langList={produceLangList} />
        ) : (
          <MainTable
            rowKey={(record) => record.project_id}
            columns={columnsOrigin}
            dataSource={searchStoryList}
          />
        )}
      </DefaultLayout>
      <ProjectRegisterModal
        visible={visible}
        setVisible={setVisible}
        langList={produceLangList}
        setTotalList={setTotalList}
      />
      <ProjectAuthModal
        visible={openStoryAuthModal}
        setVisible={setOpenStoryAuthModal}
        organizationUsers={newOrganizationUsers}
        setOrganizationUsers={setNewOrganizationUsers}
        currentProjectUsers={currentProjectUsers}
        setCurrentProjectUsers={setCurrentProjectUsers}
        projectId={currentProjectId}
      />
      <DeleteModal
        visible={deleteModal}
        setVisible={setDeleteModal}
        title={t('managerID.table.deleteConfirm')}
        label={selectedProject?.title ?? ''}
        handleDelete={handleDelete}
        loading={deleteLoading}
      />
    </>
  );
};

export default ProjectTotalListPage;

const SearchWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 35px;
  p {
    color: ${theme.colors.dark_500};
    font-weight: ${theme.fontWeights.bold};
    font-size: ${theme.fontSizes.xl};
    line-height: 36px;
    letter-spacing: 0.1px;
  }
  & > * + * {
    margin-top: 13px;
  }
`;

const SearchInputWrapper = styled.div`
  width: 320px;
  height: 40px;
`;

const TableListWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  margin-bottom: 28px;
  margin-top: 5px;
  img {
    cursor: pointer;
  }
  .category {
    padding: 10px;
    background-color: ${theme.colors.dark_50};
    border-radius: 6px;
  }
  .uncategory {
    padding: 10px;
  }
  & > * + * {
    margin-left: 8px;
  }
`;

const IDColWarpper = styled.div`
  /* display: flex; */
  align-items: center;
  text-align: center;
  & > * + * {
    margin-left: 2rem;
  }
`;

const ImageContainer = styled.div`
  text-align: center;
`;

const SpeechText = styled.p`
  color: ${theme.colors.dark_500};
  font-size: ${theme.fontSizes.normal};
  font-style: normal;
  font-weight: ${theme.fontWeights.normal};
  line-height: 24px; /* 171.429% */
  letter-spacing: 0.2px;
`;
